import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core';
import { createElement, FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { Issue, IssueComment, RequestState, User } from '../../../../../graphql/generated/graphql';
import useInputComment, { inputCommentLimit } from '../../../../../hooks/useInputComment';
import theme from '../../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../../styleUtility/flexibleLayout';
import _ModificationRequestIssueCommentItem from './ModificationRequestIssueCommentItem';

interface Props {
  issue: Pick<Issue, 'id' | 'resolved'> & {
    comments: (Pick<IssueComment, 'assetIds' | 'body' | 'createdAt'> & {
      user: Pick<User, 'name' | 'profileImageUrl'>;
    })[];
  };
  bucketId: string;
  assets: Array<FileReference>;
  modificationRequestState: RequestState;
  onRequestCreateModificationRequestItemIssueComment: (
    id: string,
    body: string,
    bucketId: string,
    assetIds: string[],
  ) => void;
  onRequestResolveModificationRequestIssue: (id: string) => void;
  onRequestReopenModificationRequestIssue: (id: string) => void;
  className?: string;
}

const ModificationRequestIssueDetail: FC<Props> = ({
  issue,
  bucketId,
  assets,
  modificationRequestState,
  onRequestCreateModificationRequestItemIssueComment,
  onRequestResolveModificationRequestIssue,
  onRequestReopenModificationRequestIssue,
  className,
}) => {
  const [inputComment, commentHandler] = useInputComment('');
  const modificationRequestApproved = modificationRequestState === RequestState.Approved;

  return (
    <Root className={className} id={`modificationRequestIssue-${issue.id}`}>
      <Head>
        <Titles>
          {issue.resolved ? (
            <Chip label={<FormattedMessage id="modification_request.modification_request_item.issue.closed" />} />
          ) : (
            <IssueOpen label={<FormattedMessage id="modification_request.modification_request_item.issue.open" />} />
          )}
          <Title variant="body2">
            <FormattedMessage id="modification_request.modification_request_item.issue.title" />
          </Title>
        </Titles>
        <FormControlLabel
          control={
            <Checkbox
              checked={issue.resolved}
              onChange={() =>
                issue.resolved
                  ? onRequestReopenModificationRequestIssue(issue.id)
                  : onRequestResolveModificationRequestIssue(issue.id)
              }
              disabled={modificationRequestApproved}
            />
          }
          label={<FormattedMessage id="modification_request.modification_request_item.issue.check" />}
        />
      </Head>

      {issue.comments.map((comment, index) => (
        <ModificationRequestIssueCommentItem
          comment={comment}
          files={assets}
          key={`ModificationRequestIssueComment-${index}`}
        />
      ))}

      {modificationRequestApproved ? null : (
        <FormArea>
          <CommentForm error={inputComment.error}>
            <Comment
              label={<FormattedMessage id="modification_request.modification_request_item.issue_comment.label" />}
              value={inputComment.body}
              variant="outlined"
              rows="4"
              rowsMax="8"
              multiline
              error={inputComment.error}
              onChange={(e) => commentHandler.setComment(e.target.value)}
            />
            <CommentSummary>
              <Typography variant="caption">
                <FormattedMessage id="modification_request.modification_request_item.input_comment.sup" />
              </Typography>
              <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
            </CommentSummary>
          </CommentForm>

          <Buttons>
            <Submit
              variant="contained"
              color="primary"
              size="small"
              disabled={inputComment.body.trim() === '' || inputComment.error}
              //* 第4引数の assetIds は Optimizer-web に添付ファイル機能がないため常に Empty Array を渡す
              onClick={() => {
                onRequestCreateModificationRequestItemIssueComment(issue.id, inputComment.body, bucketId, []);
                commentHandler.setComment('');
              }}
            >
              <FormattedMessage id="modification_request.modification_request_item.issue_comment.submit" />
            </Submit>
          </Buttons>
        </FormArea>
      )}
    </Root>
  );
};

const Root = styled('div')({
  padding: SPACING_REGULAR,
  border: `1px ${theme.palette.grey['200']} solid`,
});

const Head = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  {
    marginBottom: SPACING_UNIT * 3,
  },
);

const IssueOpen = styled(Chip)({
  background: theme.palette.error.main,
  color: '#fff',
});

const Titles = styled('div')(flexibleLayoutContainer({ alignItems: 'center' }));

const Title = styled(Typography)({
  marginLeft: SPACING_UNIT,
  fontWeight: 'bold',
});

const FormArea = styled('div')({});

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_REGULAR,
});

const Comment = styled(TextField)({
  width: '100%',
});

const CommentSummary = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  }),
  {
    marginTop: SPACING_UNIT,
  },
);

const InputCount = styled(FormHelperText)({
  margin: `0 0 0 ${SPACING_UNIT}px`,
  textAlign: 'right',
});

const Buttons = styled('div')({
  textAlign: 'right',
});

const Submit = styled(Button)({
  width: 'auto',
});

const ModificationRequestIssueCommentItem = styled(_ModificationRequestIssueCommentItem)({
  marginBottom: SPACING_REGULAR,
});

export default ModificationRequestIssueDetail;
